/**
 * N-01 «Центр уведомлений» — служебный диалог «Движ».
 *
 * Уведомления показаны как переписка с сервисом (как канал «Telegram» в
 * Telegram): шапка и обои те же, что у диалога, каждое уведомление —
 * входящее «сообщение», свежее внизу. ПС §12: match, сообщения, реакции,
 * безопасность, системные. Переход из уведомления ведёт сразу к объекту
 * в АКТУАЛЬНОМ состоянии (ПС §20.10) — это делает N-05, а не этот экран.
 *
 * Просмотр диалога считается прочтением, как в переписке: через паузу
 * видимости уходит `notifications_read` без списка id.
 */
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useNotifications } from '@entities/notification/model'
import { api, unwrap, uuid } from '@shared/api/client'
import type { Notification } from '@shared/api/types'
import { queueRepo } from '@shared/db'
import { MODERATION_REASON_TEXT } from '@shared/media/limits'
import { triggerQueue } from '@shared/sync/queue-runner'
import {
  ActionItem,
  ActionList,
  Avatar,
  Badge,
  Button,
  Icon,
  LogoTile,
  Sheet,
  VIRTUALIZE_THRESHOLD,
  VirtualList,
  cx,
  type IconName,
} from '@shared/ui'

import { KEY_ITEM_ATTR, KEY_LIST_ATTR } from '../../model/hotkeys'
import { hasInAppHistory } from '../../model/navigation-direction'
import { ScreenFrame, type ScreenFrameHandle } from '../../layouts/ScreenFrame'
import { formatDaySeparator, formatMessageTime, needsDaySeparator } from '../../lib/format'
import chatStyles from '../chats/message.module.css'
import { PushStatusBanner } from './PushPermission'
import styles from './notifications.module.css'

const SERVICE_NAME = 'Движ'

/** «Сообщение» с заголовком и двумя строками текста — оценка до измерения. */
const FEED_ROW_HEIGHT = 92

/** Пауза перед отметкой «прочитано»: диалог должен быть действительно увиден. */
const READ_DELAY_MS = 1200

export function NotificationsScreen({ embedded = false }: { embedded?: boolean }): React.ReactElement {
  const navigate = useNavigate()
  const router = useRouter()
  const { items: notifications, loaded } = useNotifications()
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null)
  const frameRef = useRef<ScreenFrameHandle>(null)

  // На mobile — вложенный экран, как диалог: «назад» историей, по deep link — в список.
  const navigateBack = (): void => {
    if (hasInAppHistory()) router.history.back()
    else void navigate({ to: '/chats', replace: true })
  }
  const goBack = (): void => {
    if (frameRef.current) frameRef.current.pop(navigateBack)
    else navigateBack()
  }

  // Реплика отдаёт свежие первыми (BS §13.1); в переписке свежее внизу.
  const feed = useMemo(() => [...notifications].reverse(), [notifications])
  const unread = notifications.reduce((sum, item) => sum + (item.read_at === null ? 1 : 0), 0)
  const virtualized = feed.length > VIRTUALIZE_THRESHOLD

  // Прочтение при просмотре — как в переписке. Свёрнутая вкладка не считается.
  useEffect(() => {
    if (!unread) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const cancel = (): void => {
      if (timer !== null) clearTimeout(timer)
      timer = null
    }
    const schedule = (): void => {
      cancel()
      if (document.visibilityState !== 'visible') return
      timer = setTimeout(() => {
        timer = null
        if (document.visibilityState === 'visible') void markAllRead().catch(() => undefined)
      }, READ_DELAY_MS)
    }
    schedule()
    document.addEventListener('visibilitychange', schedule)
    return () => {
      cancel()
      document.removeEventListener('visibilitychange', schedule)
    }
  }, [unread])

  // Короткая лента без виртуализации: при открытии — сразу в конец.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const element = scrollRef.current
    if (!element || virtualized) return
    element.scrollTop = element.scrollHeight
  }, [feed.length, virtualized])

  const open = (notification: Notification) =>
    void navigate({ to: '/notifications/$notificationId', params: { notificationId: notification.id } })

  return (
    <ScreenFrame
      ref={frameRef}
      nested={!embedded}
      onBack={embedded ? undefined : navigateBack}
      className={chatStyles['chat']}
    >
      <header className={chatStyles['header']}>
        <button
          type="button"
          className={cx(chatStyles['headerBack'], embedded && chatStyles['headerBackHidden'])}
          aria-label="Назад"
          onClick={goBack}
        >
          <Icon name="back" size={22} />
        </button>

        <div className={chatStyles['headerMain']}>
          <span className={styles['avatar']} aria-hidden>
            <LogoTile size={40} />
          </span>
          <span className={chatStyles['headerText']}>
            <span className={chatStyles['headerName']}>{SERVICE_NAME}</span>
            <span className={chatStyles['headerStatus']}>
              {unread ? `${unread} ${pluralUnread(unread)}` : 'служебные уведомления'}
            </span>
          </span>
        </div>

        <div className={chatStyles['headerActions']}>
          <Button
            icon="more"
            variant="ghost"
            aria-label="Действия с уведомлениями"
            onClick={(event) => setMenuAnchor(event.currentTarget.getBoundingClientRect())}
          />
        </div>
      </header>

      {/* N-04 / предложение N-03: статус разрешения устройства (ПС §12) */}
      <div className={styles['banner']}>
        <PushStatusBanner compact />
      </div>

      {/* D-02: `↑`/`↓` ходят по «сообщениям» */}
      <div className={styles['body']} {...{ [KEY_LIST_ATTR]: true }}>
        {virtualized ? (
          // §11.2 п. 1: длинная лента виртуализируется
          <VirtualList
            items={feed}
            getKey={(notification) => notification.id}
            estimateSize={FEED_ROW_HEIGHT}
            className={styles['virtualScroller']}
            initialAlign="end"
            ariaLabel="Уведомления"
          >
            {(notification, index) => (
              <FeedRow
                notification={notification}
                previous={feed[index - 1] ?? null}
                onOpen={() => open(notification)}
              />
            )}
          </VirtualList>
        ) : (
          <div ref={scrollRef} className={styles['scroller']} aria-label="Уведомления">
            {feed.map((notification, index) => (
              <FeedRow
                key={notification.id}
                notification={notification}
                previous={feed[index - 1] ?? null}
                onOpen={() => open(notification)}
              />
            ))}

            {!feed.length && loaded ? (
              // N-02 «Пустой центр уведомлений»
              <div className={styles['empty']}>
                <Icon name="bell" size={28} />
                <span>Здесь появятся новые совпадения, сообщения и важные события аккаунта</span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <Sheet
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        anchorRect={menuAnchor}
        title={SERVICE_NAME}
        flush
      >
        <ActionList>
          <ActionItem
            icon="check-double"
            label="Прочитать все"
            disabled={!unread}
            onClick={() => {
              setMenuAnchor(null)
              void markAllRead().catch(() => undefined)
            }}
          />
          <ActionItem
            icon="settings"
            label="Настройки уведомлений"
            onClick={() => {
              setMenuAnchor(null)
              void navigate({ to: '/settings/$section', params: { section: 'notifications' } })
            }}
          />
        </ActionList>
      </Sheet>
    </ScreenFrame>
  )
}

function pluralUnread(count: number): string {
  return count % 10 === 1 && count % 100 !== 11 ? 'непрочитанное' : 'непрочитанных'
}

async function markAllRead(): Promise<void> {
  await queueRepo.enqueue({
    op_id: uuid(),
    kind: 'notifications_read',
    notification_ids: [],
    created_at: new Date().toISOString(),
    attempt_count: 0,
    next_attempt_at: null,
    last_error: null,
    claimed_by: null,
    claimed_at: null,
    idempotency_key: null,
  })
  triggerQueue()
}

/* ===================================================================== */
/* «Сообщение» сервиса                                                    */
/* ===================================================================== */

function FeedRow({
  notification,
  previous,
  onOpen,
}: {
  notification: Notification
  previous: Notification | null
  onOpen: () => void
}) {
  const view = describeNotification(notification)
  const unread = notification.read_at === null

  return (
    <>
      {needsDaySeparator(previous?.created_at ?? null, notification.created_at) ? (
        <div className={styles['day']}>{formatDaySeparator(notification.created_at)}</div>
      ) : null}

      <button
        type="button"
        className={cx(styles['bubble'], unread && styles['bubbleUnread'])}
        {...{ [KEY_ITEM_ATTR]: true }}
        onClick={onOpen}
      >
        <span className={cx(styles['icon'], styles[view.tone])}>
          {view.counterpart ? (
            <Avatar
              name={view.counterpart.display_name}
              photo={view.counterpart.photo}
              availability={view.counterpart.availability}
              size="md"
            />
          ) : (
            <Icon name={view.icon} size={20} />
          )}
        </span>

        <span className={styles['content']}>
          <span className={styles['title']}>{view.title}</span>
          <span className={styles['text']}>{view.text}</span>
          <LoginCode notification={notification} />
        </span>

        <span className={styles['meta']}>
          <span>{formatMessageTime(notification.created_at)}</span>
          {unread ? <Badge dot /> : null}
        </span>
      </button>
    </>
  )
}

/**
 * Изменение v1.12. Код для входа, доставленный в приложение: в самом уведомлении его нет —
 * устройство забирает код отдельным запросом (BS §5.2). Показываем прямо в ленте, как
 * Telegram показывает код сообщением от сервиса: ради него уведомление и пришло.
 */
function LoginCode({ notification }: { notification: Notification }): React.ReactElement | null {
  const payload = notification.payload as Record<string, unknown>
  const challengeId = typeof payload['challenge_id'] === 'string' ? payload['challenge_id'] : null
  const expired = typeof payload['expires_at'] === 'string' && Date.parse(payload['expires_at']) < Date.now()

  const { data } = useQuery({
    queryKey: ['login-code', challengeId],
    enabled:
      notification.type === 'security' &&
      payload['event'] === 'login_code' &&
      challengeId !== null &&
      !expired,
    /*
     * Код у challenge не меняется, поэтому запрашивается один раз: иначе каждое открытие
     * N-01 в течение десяти минут заново дёргало бы сервер. Повтор не нужен и при ошибке —
     * 404 означает, что код уже использован или погашен переключением на SMS.
     */
    staleTime: Infinity,
    retry: false,
    queryFn: async () =>
      unwrap(
        await api.GET('/auth/challenges/{challengeId}/code', {
          params: { path: { challengeId: challengeId as string } },
        }),
      ),
  })

  if (!data) return null
  return <span className={styles['code']}>{data.code.replace(/(\d{3})(\d{3})/, '$1 $2')}</span>
}

interface NotificationView {
  title: string
  text: string
  icon: IconName
  tone: 'iconAccent' | 'iconInfo' | 'iconWarning' | 'iconNeutral'
  counterpart?: {
    display_name: string | null
    availability: 'available' | 'unavailable' | 'deleted'
    photo: never
  } | null
}

/**
 * BS §13.1: селектор payload — поле `type` САМОГО уведомления, а не payload
 * (в payload его нет). Раньше подпись строилась по `payload.type`, поэтому
 * каждое уведомление показывалось как «Служебное сообщение».
 *
 * BS §13.4: payload privacy-safe — в нём НЕТ текста сообщения, поэтому
 * подпись строится по типу события, а не по содержимому. Неизвестный тип
 * (правило открытых перечислений BS §16.2) — нейтральная запись.
 */
export function describeNotification(notification: Notification): NotificationView {
  const payload = notification.payload as Record<string, unknown>
  const kind: string = notification.type

  switch (kind) {
    case 'match': {
      const counterpart = (payload['counterpart'] as NotificationView['counterpart']) ?? null
      const name = counterpart?.availability === 'available' ? counterpart.display_name : null
      return {
        title: 'Новое совпадение',
        text: name
          ? `${name} тоже за движ — можно начинать общение`
          : 'Взаимное «Го» — можно начинать общение',
        icon: 'matches',
        tone: 'iconAccent',
        counterpart,
      }
    }

    case 'message': {
      const sender = (payload['sender'] as NotificationView['counterpart']) ?? null
      const name = sender?.availability === 'available' ? sender.display_name : null
      return {
        title: name ? `Сообщение от ${name}` : 'Новое сообщение',
        text: MESSAGE_TYPE_TEXT[String(payload['message_type'])] ?? 'Откройте диалог, чтобы прочитать',
        icon: 'chats',
        tone: 'iconAccent',
        counterpart: sender,
      }
    }

    case 'reaction': {
      const actor = (payload['actor'] as NotificationView['counterpart']) ?? null
      const name = actor?.availability === 'available' ? actor.display_name : null
      const reaction = String(payload['reaction'] ?? '').trim()
      return {
        title: 'Реакция на сообщение',
        text: `${name ?? 'Собеседник'} отреагировал(а) ${reaction ? `«${reaction}»` : ''} на ваше сообщение`.replace(
          '  ',
          ' ',
        ),
        icon: 'smile',
        tone: 'iconNeutral',
        counterpart: actor,
      }
    }

    case 'security': {
      const event = String(payload['event'] ?? '')
      return {
        title: SECURITY_TITLE[event] ?? 'Безопасность аккаунта',
        text: describeSecurity(payload),
        icon: event === 'login_code' ? 'lock' : 'shield',
        tone: event === 'login_code' ? 'iconAccent' : 'iconWarning',
      }
    }

    case 'moderation':
      return {
        title: MODERATION_TITLE[String(payload['event'])] ?? 'Решение модерации',
        text: describeModeration(payload),
        icon: 'alert',
        tone: 'iconWarning',
      }

    case 'system':
      return {
        title: SYSTEM_TITLE[String(payload['event'])] ?? 'Служебное сообщение',
        text: SYSTEM_TEXT[String(payload['event'])] ?? 'Откройте, чтобы посмотреть',
        icon: 'info',
        tone: 'iconInfo',
      }

    default:
      return {
        title: 'Служебное сообщение',
        text: 'Откройте, чтобы посмотреть',
        icon: 'info',
        tone: 'iconInfo',
      }
  }
}

const MESSAGE_TYPE_TEXT: Record<string, string> = {
  text: 'Откройте диалог, чтобы прочитать',
  image: 'Прислали фотографию',
  video: 'Прислали видео',
  voice: 'Прислали голосовое сообщение',
}

const PLATFORM_TEXT: Record<string, string> = {
  android: 'приложение Android',
  web: 'браузер',
}

const SECURITY_TITLE: Record<string, string> = {
  new_login: 'Вход в аккаунт',
  session_revoked: 'Сеанс завершён',
  identity_added: 'Новый способ входа',
  identity_removed: 'Способ входа удалён',
  settings_changed: 'Настройки безопасности изменены',
  login_code: 'Код для входа',
}

const SECURITY_TEXT: Record<string, string> = {
  new_login: 'Выполнен вход с нового устройства',
  session_revoked: 'Сеанс на другом устройстве завершён',
  identity_added: 'Добавлен новый способ входа',
  identity_removed: 'Удалён способ входа',
  settings_changed: 'Изменены важные настройки аккаунта',
  login_code: 'Кто-то входит в ваш аккаунт',
}

/** Строка `str` из payload, если она непустая. */
function text(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key]
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/**
 * Изменение v1.12. Уведомление о безопасности отвечает на «кто, откуда и когда» само —
 * как в Telegram, где письмо о входе перечисляет устройство, место и адрес. Раньше здесь
 * были только платформа и город, и из уведомления нельзя было понять, свой ли это вход.
 */
export function describeSecurity(payload: Record<string, unknown>): string {
  const event = String(payload['event'] ?? '')
  const base = SECURITY_TEXT[event] ?? 'Проверьте недавние действия в аккаунте'

  const device =
    text(payload, 'device_name') ??
    text(payload, 'client') ??
    PLATFORM_TEXT[String(payload['platform'] ?? '')]
  const place = [text(payload, 'city'), text(payload, 'region'), text(payload, 'country')].filter(
    (part, index, all): part is string => part !== null && all.indexOf(part) === index,
  )
  const where = [device, place.join(', ') || null, text(payload, 'ip')].filter(Boolean).join(' · ')

  if (event === 'login_code') {
    return where ? `${base}: ${where}. Если это не вы — никому не сообщайте код` : base
  }
  if (!where) return base
  return event === 'new_login'
    ? `${base}: ${where}. Если это не вы — завершите сеанс в настройках`
    : `${base} (${where})`
}

const MODERATION_TITLE: Record<string, string> = {
  photo_rejected: 'Фотография не прошла проверку',
  profile_field_rejected: 'Текст профиля не прошёл проверку',
  sanction_applied: 'На аккаунт наложено ограничение',
  sanction_revoked: 'Ограничение снято',
  report_resolved: 'Ваша жалоба рассмотрена',
  appeal_decided: 'Решение по апелляции',
}

const FIELD_TEXT: Record<string, string> = {
  display_name: 'имя',
  bio: 'рассказ о себе',
  occupation: 'род занятий',
}

const SANCTION_TEXT: Record<string, string> = {
  warning: 'предупреждение',
  read_only: 'ограничена отправка сообщений',
  hidden: 'профиль скрыт из рекомендаций',
  banned: 'доступ к аккаунту заблокирован',
}

const OUTCOME_TEXT: Record<string, string> = {
  accepted: 'Апелляция удовлетворена — ограничение снято',
  rejected: 'Апелляция отклонена — ограничение остаётся в силе',
  dismissed: 'Апелляция оставлена без рассмотрения',
}

/** Понятный текст вместо кода: категория причины, имя поля, вид ограничения. */
function describeModeration(payload: Record<string, unknown>): string {
  const event = String(payload['event'])
  const reason = MODERATION_REASON_TEXT[String(payload['reason_category'] ?? '')]

  switch (event) {
    case 'photo_rejected':
      return reason
        ? `${reason}. Замените фото в разделе «Фотографии»`
        : 'Замените фото в разделе «Фотографии»'
    case 'profile_field_rejected': {
      const field = FIELD_TEXT[String(payload['field'] ?? '')]
      const what = field ? `Поле «${field}»` : 'Поле профиля'
      return reason
        ? `${what}: ${reason.toLowerCase()}. Отредактируйте его в профиле`
        : `${what} отклонено. Отредактируйте его в профиле`
    }
    case 'sanction_applied': {
      const type = SANCTION_TEXT[String(payload['sanction_type'] ?? '')] ?? 'часть функций ограничена'
      const capitalised = type.charAt(0).toUpperCase() + type.slice(1)
      return payload['appealable'] === true
        ? `${capitalised}. Решение можно обжаловать в настройках`
        : `${capitalised}. Подробности — в разделе «Ограничения аккаунта»`
    }
    case 'sanction_revoked':
      return 'Аккаунт снова работает в обычном режиме'
    case 'report_resolved':
      return 'Решение по обращению принято. Детали не раскрываем'
    case 'appeal_decided':
      return OUTCOME_TEXT[String(payload['outcome'] ?? '')] ?? 'Откройте, чтобы посмотреть решение'
    default:
      return 'Есть решение по вашему профилю'
  }
}

const SYSTEM_TITLE: Record<string, string> = {
  export_ready: 'Архив данных готов',
  ticket_replied: 'Ответ поддержки',
  consent_required: 'Документы обновились',
}

const SYSTEM_TEXT: Record<string, string> = {
  export_ready: 'Ссылка на архив с вашими данными действует 24 часа',
  ticket_replied: 'Поддержка ответила на ваше обращение',
  consent_required: 'Нужно принять обновлённые условия, чтобы продолжить',
}
